import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

/**
 * Pushes this device's clock to the current browser time. On mount it reads the
 * device epoch (seconds) over serial and shows it alongside the drift versus the
 * browser now, so the operator can see how far off the device clock has run.
 */
export default function TimeSyncCard({ serialClient, onError = undefined }) {
  const [deviceEpoch, setDeviceEpoch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const epoch = await serialClient.getTime();
      setDeviceEpoch(epoch);
    } catch (err) {
      const message = err.message || 'Failed to read device time';
      setError(message);
      if (onError) onError(message);
    }
  }, [serialClient, onError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSync = useCallback(async () => {
    setBusy(true);
    setSuccess(false);
    setError(null);
    try {
      await serialClient.syncTime();
      setSuccess(true);
      await refresh();
    } catch (err) {
      const message = err.message || 'Time sync failed';
      setError(message);
      if (onError) onError(message);
    } finally {
      setBusy(false);
    }
  }, [serialClient, onError, refresh]);

  let deviceTimeLabel;
  if (deviceEpoch === null) {
    deviceTimeLabel = 'Device clock not set';
  } else {
    const formatted = new Date(deviceEpoch * 1000).toLocaleString();
    const drift = Math.round(Date.now() / 1000) - deviceEpoch;
    let driftLabel;
    if (drift > 0) {
      driftLabel = `${drift}s behind`;
    } else if (drift < 0) {
      driftLabel = `${-drift}s ahead`;
    } else {
      driftLabel = 'in sync';
    }
    deviceTimeLabel = `${formatted} (${driftLabel})`;
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Set Time
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Push this device's clock to the current time.
        </Typography>

        <Typography variant="body2" sx={{ mb: 2 }}>
          {deviceTimeLabel}
        </Typography>

        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <AccessTimeIcon />}
          onClick={handleSync}
          disabled={busy}
        >
          {busy ? 'Syncing...' : 'Sync time now'}
        </Button>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Device time synced.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

TimeSyncCard.propTypes = {
  serialClient: PropTypes.object.isRequired,
  onError: PropTypes.func,
};
